import { message } from 'antd';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import {
  addFilesToShare,
  AddFilesToShareParams,
  createShare,
  createShareKey,
  CreateShareParams,
} from '~src/services/api/shares';
import { DB_STATE } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import { useProjectId } from '~src/hooks/useProjectId';

type UseDoShareFilesReturn = {
  doShareFiles: ({
    shareData,
    fileIds,
    emails,
  }: DoShareFilesParams) => Promise<void>;
};

interface DoShareFilesParams {
  shareData: Omit<CreateShareParams['data'], 'project_id'>;
  fileIds: AddFilesToShareParams['fileIds'];
  emails: string[];
}
/** Create Share object and add selected files;
 * to be used for "Share" function
 */
export const useDoShareFiles = (): UseDoShareFilesReturn => {
  const intl = useIntl();
  const projectId = useProjectId();

  const doShareFiles: UseDoShareFilesReturn['doShareFiles'] = useCallback(
    async ({ shareData, fileIds, emails }: DoShareFilesParams) => {
      if (!projectId) throw new Error('Missing project id');

      try {
        // create Share object
        const share = await createShare({
          data: { ...shareData, project_id: projectId },
        });

        // add selected files to share
        // TODO: unlock when api fixed
        await addFilesToShare({ shareId: share.id, fileIds });

        // create ShareKey(s), which triggers email sending
        await Promise.all(
          emails.map(async (email) => {
            await createShareKey({
              data: {
                share_id: share.id,
                email,
                title: '',
                state: DB_STATE.COMPLETED,
              },
            });
          })
        );

        message.success(intl.formatMessage({ id: 'share-success' }));
      } catch (err) {
        appLog('doShareFiles -> err', err);
        message.error(intl.formatMessage({ id: 'share-failed' }));
      }
    },
    [projectId, intl]
  );

  return { doShareFiles };
};
